<template>
  <v-row no-gutters>
    <ConvertTypeADialog
      v-model="showConvertToADialog"
      @onConvertOfferType="handlerConvertToAOffer"
    />
    <v-col cols="12" lg="8" md="8">
      <p class="text-h2">{{ canEditOffer ? $t('SHIPPING_TITLE') : $t('STEPPER_STEP1_LABEL') }}</p>

      <p v-if="canEditOffer" v-html="$t('SHIPPING_SUBTITLE')" class="text-body-1 grey-font mb-8" />

      <v-expansion-panels flat>
        <v-expansion-panel v-for="(product, i) in productIdList" :key="'product-' + product.id">
          <v-divider v-if="i === 0" />
          <Product
            :id="product.id"
            :desc="product.desc"
            @onChangeZoneOfferInvalidOnProduct="handleChangeZoneOfferInvalidOnProduct"
            @onChangeProductOfferInvalidOnProduct="handleChangeProductOfferInvalidOnProduct"
          />
          <v-divider />
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>

    <SummaryCard>
      <template v-slot:save-and-reset-offer-buttons>
        <SaveAndResetOfferButtons @onSaveClicked="saveOffer" @onResetClicked="resetOffer" />
      </template>

      <template v-slot:change-type-button>
        <ChangeTypeButton @onChangeType="handleChangeType" />
      </template>

      <template v-slot:change-discount-button>
        <MaxDiscountsButton/>
      </template>

      <template v-slot:validations-button>
        <ActivateValidationsButton v-if="isUserAnEvaluator" @updateErrors="() => {}" />
      </template>

      <template v-slot:offer-status-button>
        <UpdateOfferStatusButton />
      </template>
    </SummaryCard>
  </v-row>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import {
  GET_OFFER_ITEM,
  GET_PRODUCT_ID_LIST,
  GET_PRODUCT_TABLE,
  GET_PRODUCT,
} from '@/store/modules/offer/getters'
import { SET_TYPE_AND_VAL_STATUS_OFFER } from '@/store/modules/offer/mutations'
import { RESET_PRODUCTS_OFFER_PRICES } from '@/store/modules/offer/actions'
import { OFFER_TYPE } from '@/helpers/constants'
import SummaryCard from '@/components/summary-card'
import SaveAndResetOfferButtons from '@/components/buttons/SaveAndResetOfferButtons'
import ChangeTypeButton from '@/components/buttons/ChangeTypeButton'
import ActivateValidationsButton from '@/components/buttons/ActivateValidationsButton'
import MaxDiscountsButton from '@/components/buttons/MaxDiscountsButton'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'
import Product from '@/containers/offer-form/shipping/Product'
import ConvertTypeADialog from '@/containers/offer-form/shipping/ConvertTypeADialog'

export default {
  name: 'Shipping',
  components: {
    SummaryCard,
    ActivateValidationsButton,
    ChangeTypeButton,
    SaveAndResetOfferButtons,
    UpdateOfferStatusButton,
    Product,
    ConvertTypeADialog,
    MaxDiscountsButton,
  },
  data() {
    return {
      showConvertToADialog: false,
      indexedProductErrors: {},
      id: '',
    }
  },
  computed: {
    ...mapGetters('offer', [
      GET_OFFER_ITEM,
      GET_PRODUCT_ID_LIST,
      GET_PRODUCT_TABLE,
      GET_PRODUCT,
      'canEditOffer',
    ]),
    ...mapGetters('roles', ['getRole']),
    ...mapState('process', ['areProductsPricesValid']),
    // Offer is invalid if any product signals zone or product pricing errors
    isOfferInvalid() {
      return Object.values(this.indexedProductErrors || {}).some(Boolean)
    },
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    europaLigthSelected() {
      return this[GET_PRODUCT]('ELIGHT')?.isPreSelected
    },
    isUserAnEvaluator() {
      return this.getRole === 'evaluator'
    },
    productIdList() {
      return this[GET_PRODUCT_ID_LIST]
        .filter(productId => {
          const product = this[GET_PRODUCT](productId);
          return product?.isPreSelected && !['C14E', 'ELIGHT'].includes(productId);
        })
        .map(productId => {
          const product = this[GET_PRODUCT](productId);
          return {
            id: productId,
            desc: product?.desc_key || productId
          };
        });
    }


  },
  methods: {
    ...mapActions('offer', [RESET_PRODUCTS_OFFER_PRICES]),
    ...mapMutations('offer', [SET_TYPE_AND_VAL_STATUS_OFFER]),
    ...mapActions('alerts', ['createAlert']),
    ...mapMutations('alerts', ['removeAlert']),
    ...mapMutations('process', ['setAreProductsPricesValid']),
    saveOffer() {
      this.$emit('onSaveClicked')
    },
    resetOffer() {
      this.$emit('onResetClicked')
    },
    addAlert(product, type) {
      if (!['zone', 'product'].includes(type)) {
        return
      }
      const translKey = type === 'zone' ? 'ALERT_ZONE_ERROR' : 'ALERT_PRODUCT_ERROR'
      const descKey = this[GET_PRODUCT](product)?.desc_key || product
      this.createAlert({
        id: `shipping_${type}_${product}`,
        type: 'error',
        htmlContentKey: translKey,
        htmlContentParams: { productKey: `PRODUCT_${descKey}` },
        dismissible: false,
        manualDismiss: true,
      })
    },
    delAlert(product, type) {
      if (!['zone', 'product'].includes(type)) {
        return
      }
      this.removeAlert(`shipping_${type}_${product}`)
    },
    handleChangeIsProductInvalid(productId, isInvalid, errorType) {
      // Use $set to ensure Vue reactivity detects the change
      this.$set(this.indexedProductErrors, productId, isInvalid)
      if (isInvalid) {
        this.addAlert(productId, errorType)
      } else {
        this.delAlert(productId, errorType)
      }
    },
    handleChangeZoneOfferInvalidOnProduct(productId, isInvalid) {
      this.handleChangeIsProductInvalid(productId, isInvalid, 'zone')
    },
    handleChangeProductOfferInvalidOnProduct(productId, isInvalid) {
      this.handleChangeIsProductInvalid(productId, isInvalid, 'product')
    },
    initializeProductsFeaturesList() {
      // Clear alerts for ALL products (including ones deselected since last visit)
      this[GET_PRODUCT_ID_LIST].forEach(productId => {
        this.delAlert(productId, 'zone')
        this.delAlert(productId, 'product')
      })
      // Initialize error tracking only for currently visible products
      this.productIdList.forEach(({ id }) => {
        this.$set(this.indexedProductErrors, id, false)
      })
    },
    handleChangeType() {
      if (this.offerType === OFFER_TYPE.C) {
        this.showConvertToADialog = true
      } else {
        this[SET_TYPE_AND_VAL_STATUS_OFFER]({ type: OFFER_TYPE.C })
      }
    },
    handlerConvertToAOffer() {
      this[RESET_PRODUCTS_OFFER_PRICES]()
      this[SET_TYPE_AND_VAL_STATUS_OFFER]({ type: OFFER_TYPE.A })
    },
  },
  created() {
    this.initializeProductsFeaturesList()
    // Initialize process flag and CTA disabled state based on current errors
    this.setAreProductsPricesValid(!this.isOfferInvalid)
    this.$emit('onChangeIsContinueForbidden', this.isOfferInvalid)
  },
  watch: {
    areProductsPricesValid() {
      this.$emit('onChangeIsContinueForbidden', !this.areProductsPricesValid)
    },
    isOfferInvalid(newVal) {
      // When any product reports errors, update store and CTA state
      this.setAreProductsPricesValid(!newVal)
      this.$emit('onChangeIsContinueForbidden', newVal)
    },
  },
}
</script>
